// AgentMatkaPanel — /games/matka_king on the agent app (plan M-60…M-63, P2 slice).
// What a broker needs at a glance: what Matka earned him (his BOOKED share, from the
// settlement snapshots), how the partnership chain gives it to him, and the hisaab
// of any player under him. Playing for a player and the downline bet sheet land in
// the next phase on this same page.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { MatkaHisaab } from '@baaligames/shared-ui/admin/components/MatkaHisaab';
import { MatkaPlayForPlayer, MatkaBook } from '@baaligames/shared-ui/admin/components/MatkaPlayForPlayer';

type Overview = { days: number; from: string; total: number; byDay: Array<{ date: string; share: number; legs: number; players: number }>;
  partnership: { myPct: number; level: number | null; subAgents: Array<{ id: number; username: string; level: number; pct: number; youKeepPct: number }> } };

const inr = (n: number) => `₹${Math.abs(Number(n || 0)).toLocaleString('en-IN')}`;
const signed = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '') + inr(n);
const tone = (n: number) => (n > 0 ? 'text-green-700 dark:text-green-300' : n < 0 ? 'text-red-600 dark:text-red-300' : 'text-arena-500');
const LEVEL: Record<number, string> = { 1: 'Super Master', 2: 'Master', 3: 'Agent' };

export default function AgentMatkaPanel() {
  const [days, setDays] = useState(7);
  const [o, setO] = useState<Overview | null>(null);
  const [tab, setTab] = useState<'overview' | 'results' | 'play' | 'book' | 'hisaab'>('overview');
  useEffect(() => { adminApi.get<Overview>(`/admin/matka-hisaab/overview?days=${days}`).then(setO).catch(() => setO(null)); }, [days]);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/games" className="w-9 h-9 grid place-items-center rounded-full bg-arena-100 dark:bg-arena-700/60"><Icon name="arrow_back" size={20} /></Link>
        <div className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/25 to-yellow-600/15 text-amber-700 dark:text-amber-300"><Icon name="casino" size={22} filled /></div>
        <div className="flex-1 min-w-0"><h1 className="text-lg font-black text-arena-800 dark:text-neon-100">Matka King</h1><div className="text-xs text-arena-500 dark:text-neon-200/60">Your share, today's results and charts, play for a player, your players' bets and hisaab.</div></div>
      </div>
      <div className="flex flex-wrap gap-1">
        {([['overview', 'My share'], ['results', 'Results & charts'], ['play', 'Play for a player'], ['book', "My players' bets"], ['hisaab', "Players' hisaab"]] as const).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${tab === k ? 'bg-amber-600 text-white' : 'bg-arena-100 dark:bg-arena-700/60 text-arena-800 dark:text-neon-100'}`}>{l}</button>))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-3">
          <section className="rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <div className="font-black">What Matka earned you</div>
              <div className="ml-auto flex gap-1">{[7, 15, 30, 180].map((d) => <button key={d} type="button" onClick={() => setDays(d)} className={`px-2 py-1 rounded-lg text-[11px] font-bold ${days === d ? 'bg-amber-600 text-white' : 'bg-arena-100 dark:bg-arena-700/60'}`}>{d === 180 ? '6 mo' : `${d} d`}</button>)}</div>
            </div>
            {o ? (
              <>
                <div className={`font-black text-3xl tabular-nums ${tone(o.total)}`}>{signed(o.total)}</div>
                <div className="text-[11px] text-arena-500 dark:text-neon-200/60">Since {o.from}. Green = your players lost and your share came to you; red = they won and you paid your share.</div>
                <div className="flex items-end gap-1 h-16">
                  {o.byDay.map((d) => { const max = Math.max(1, ...o.byDay.map((x) => Math.abs(x.share))); const h = Math.max(3, Math.round(Math.abs(d.share) / max * 56)); return (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${signed(d.share)} · ${d.legs} legs · ${d.players} players`}>
                      <div className={`w-full rounded-t ${d.share >= 0 ? 'bg-green-500/70' : 'bg-red-500/70'}`} style={{ height: h }} /><div className="text-[9px] text-arena-400">{d.date.slice(5)}</div>
                    </div>); })}
                  {!o.byDay.length && <div className="text-xs text-arena-500">Nothing settled in this period.</div>}
                </div>
              </>
            ) : <div className="text-sm text-arena-500">Loading…</div>}
          </section>

          {o && (
            <section className="rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-4 space-y-2">
              <div className="font-black">How your share works</div>
              <div className="text-sm">Your partnership: <b>{o.partnership.myPct}%</b>{o.partnership.level ? ` (${LEVEL[o.partnership.level] || `level ${o.partnership.level}`})` : ''}.</div>
              <div className="text-[12px] text-arena-600 dark:text-neon-200/80">Every player's result is split on this ladder: the player's own agent keeps his %, each one above keeps the difference up to his own %, and the house keeps the rest. Multipliers are the same for everyone — the % is the only thing that differs.</div>
              <ul className="text-sm space-y-1">
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" />Your direct players → you keep <b>{o.partnership.myPct}%</b> of their result.</li>
                {o.partnership.subAgents.map((s) => (
                  <li key={s.id} className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-arena-400" />{LEVEL[s.level] || 'Agent'} <b>{s.username}</b> is at {s.pct}% → you keep <b>{s.youKeepPct}%</b> of his players' results.</li>))}
              </ul>
              <div className="text-[11px] text-arena-500 dark:text-neon-200/60">Example: a player under you loses ₹1,000 → you get ₹{Math.round(10 * o.partnership.myPct)}. If he wins ₹1,000 → you pay ₹{Math.round(10 * o.partnership.myPct)}. Every figure on this page is what was actually booked, leg by leg.</div>
            </section>
          )}
        </div>
      )}
      {tab === 'results' && <AgentResults />}
      {tab === 'play' && <MatkaPlayForPlayer viewer="agent" />}
      {tab === 'book' && <MatkaBook />}
      {tab === 'hisaab' && <MatkaHisaab viewer="agent" />}
    </div>
  );
}

// ── RESULTS & CHARTS (the old master end's results page + doublechart/singlechart)
// Same board the player sees: every game's open-jodi-close per day, singles by
// slot, and a weeks × Mon–Sun chart per game. Read-only.
type ResultsResp = { from: string; to: string;
  doubles: Array<{ market_id: number; name: string; result_date: string; open_pana: string | null; open_digit: string | null; close_pana: string | null; close_digit: string | null; jodi: string | null; red_jodi: boolean; is_holiday: boolean }>;
  singles: Array<{ market_id: number; name: string; result_date: string; slot: number; slot_time: string; pana: string | null; digit: string | null; is_holiday: boolean }> };
const hhmm = (t?: string | null) => { if (!t) return '—'; const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; };
const box = 'rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-3';
const chip = (on: boolean) => `px-2.5 py-1 rounded-lg text-[11px] font-bold ${on ? 'bg-amber-600 text-white' : 'bg-arena-100 dark:bg-arena-700/60 text-arena-800 dark:text-neon-100'}`;

function AgentResults() {
  const [r, setR] = useState<ResultsResp | null>(null);
  const [days, setDays] = useState(7);
  const [chart, setChart] = useState<{ id: number; name: string } | null>(null);
  const [c, setC] = useState<any | null>(null);
  useEffect(() => { setR(null); adminApi.get<ResultsResp>(`/admin/matka-agent/results?days=${days}`).then(setR).catch(() => setR({ from: '', to: '', doubles: [], singles: [] })); }, [days]);
  useEffect(() => { setC(null); if (!chart) return; adminApi.get<any>(`/admin/matka-agent/chart?marketId=${chart.id}`).then(setC).catch(() => setC({ error: true })); }, [chart]);
  if (chart) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><button type="button" aria-label="Back" onClick={() => setChart(null)} className="w-9 h-9 grid place-items-center rounded-full bg-arena-100 dark:bg-arena-700/60"><Icon name="arrow_back" size={20} /></button><div className="font-black">{chart.name} · chart</div></div>
        {!c ? <div className="text-sm text-arena-500 py-6 text-center">Loading…</div> : c.error ? <div className="text-sm text-red-600">Could not load the chart.</div> : c.game.kind === 'double' ? (
          <div className={`${box} overflow-x-auto`}>
            <table className="text-[11px] font-mono">
              <thead><tr className="text-arena-500"><th className="px-1 text-left">Week</th>{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <th key={d} className="px-1">{d}</th>)}</tr></thead>
              <tbody>{[...c.weeks].reverse().map((w: any) => (
                <tr key={w.monday} className="border-t border-arena-100 dark:border-arena-700/40">
                  <td className="px-1 text-arena-500 whitespace-nowrap">{w.monday.slice(5)}</td>
                  {w.days.map((d: any) => (
                    <td key={d.date} className={`px-1 py-1 text-center ${d.red ? 'text-red-600 dark:text-red-400 font-black' : ''}`}>
                      {d.holiday ? <span className="text-arena-300">**</span> : d.openPana ? <><div className="text-[9px] leading-none opacity-70">{d.openPana}</div><div className="font-black text-sm leading-tight">{d.jodi ?? `${d.openDigit ?? '*'}*`}</div><div className="text-[9px] leading-none opacity-70">{d.closePana ?? '***'}</div></> : <span className="text-arena-300">·</span>}
                    </td>))}
                </tr>))}</tbody>
            </table>
            <div className="text-[10px] text-arena-500 mt-1">Each cell: open patti · jodi · close patti. Red = a double / cut jodi.</div>
          </div>
        ) : (
          <div className={`${box} space-y-1`}>
            {c.days.map((d: any) => (
              <div key={d.date} className="flex items-center gap-1 text-[11px]"><span className="w-12 text-arena-500">{d.date.slice(5)}</span>
                <div className="flex flex-wrap gap-1">{d.slots.map((s: any) => <span key={s.slot} className="rounded bg-amber-50 dark:bg-arena-700/60 px-1 font-mono">{s.pana ? `${s.pana}-${s.digit}` : '·'}</span>)}</div></div>))}
          </div>
        )}
      </div>
    );
  }
  if (!r) return <div className="text-sm text-arena-500 py-6 text-center">Loading…</div>;
  const dates = [...new Set([...r.doubles.map((x) => x.result_date), ...r.singles.map((x) => x.result_date)])].sort().reverse();
  const gameMap = new Map<number, string>();
  for (const x of r.doubles) gameMap.set(x.market_id, x.name);
  for (const x of r.singles) gameMap.set(x.market_id, x.name);
  const games: Array<{ id: number; name: string }> = [...gameMap.entries()].map(([id, name]) => ({ id, name }));
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        {[7, 15, 30].map((d) => <button key={d} type="button" onClick={() => setDays(d)} className={chip(days === d)}>{d} days</button>)}
        <span className="text-[11px] text-arena-500 dark:text-neon-200/60 ml-1">Charts:</span>
        {games.map((g) => <button key={g.id} type="button" onClick={() => setChart({ id: g.id, name: g.name })} className={chip(false)}>{g.name}</button>)}
      </div>
      {dates.map((d) => (
        <section key={d} className={box}>
          <div className="font-black text-sm mb-1">{d}</div>
          {r.doubles.filter((x) => x.result_date === d).map((x) => (
            <div key={`d${x.market_id}`} className="flex items-center gap-2 py-1 text-sm">
              <span className="flex-1 truncate">{x.name}</span>
              <span className={`font-mono font-black tracking-wider ${x.red_jodi ? 'text-red-600 dark:text-red-400' : ''}`}>
                {x.is_holiday ? 'holiday' : `${x.open_pana ?? '***'}-${x.open_digit ?? '*'}${x.close_digit ?? '*'}-${x.close_pana ?? '***'}`}
              </span>
            </div>
          ))}
          {[...new Set(r.singles.filter((x) => x.result_date === d).map((x) => x.market_id))].map((mid) => {
            const rows = r.singles.filter((x) => x.result_date === d && x.market_id === mid);
            return (
              <div key={`s${mid}`} className="py-1">
                <div className="text-sm">{rows[0].name}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {rows.map((x) => (
                    <span key={x.slot} className="rounded-lg bg-amber-50 dark:bg-arena-700/60 px-1.5 py-0.5 font-mono text-xs">
                      <span className="text-arena-400">{hhmm(x.slot_time)} </span><b>{x.pana ? `${x.pana}-${x.digit}` : '—'}</b>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      ))}
      {!dates.length && <div className="text-sm text-arena-500 py-6 text-center">No results yet.</div>}
    </div>
  );
}

